import { injectable, inject } from 'inversify';
import { DesignImplementationFlowRepository } from "../../infrastructure/repositories/DesignImplementationFlowRepository";
import { TYPES } from '../../../../types/DITypes';

/**
 * Flow query criteria for filtering
 */
export interface FlowQueryCriteria {
  projectId?: string;
  status?: string;
  createdBy?: string;
  artifactType?: string;
  hasConformanceIssues?: boolean;
}

/**
 * Flow view model for read operations
 */
export interface FlowView {
  flowId: string;
  projectId: string;
  status: string;
  createdBy: string;
  createdAt: string;
  completedAt?: string;
  
  // Meta-model binding
  metaModelBinding?: {
    metamodelId: string;
    boundAt: string;
    validationStatus: string;
  };
  
  // Design specification
  designSpecification?: {
    designId: string;
    designType: string;
    domainContext: string;
  };
  
  // Progress metrics
  progressMetrics: {
    totalArtifacts: number;
    completedArtifacts: number;
    artifactsInProgress: number;
    averageConformance: number;
    averageDesignCoverage: number;
  };
  
  // Blocked artifacts summary
  blockedArtifacts: Array<{
    artifactId: string;
    artifactName: string;
    blockReason: string;
    severity: string;
  }>;
  
  // Individual artifacts
  artifacts: Array<{
    id: string;
    name: string;
    type: string;
    version?: string;
    status: string;
    conformanceScore?: {
      metaModelScore: number;
      designCoverageScore: number;
      overallScore: number;
      confidence: string;
    };
    healthStatus: {
      buildStatus: string;
      testStatus: string;
      deploymentStatus: string;
      lastHealthCheck: string;
    };
    tags: string[];
  }>;
}

/**
 * Query result with pagination
 */
export interface FlowQueryResult {
  flows: FlowView[];
  totalCount: number;
  hasMore: boolean;
}

/**
 * Design Implementation Flow Read Service
 * 
 * Following GTD CQRS pattern for read operations.
 * Provides optimized queries for flow monitoring and reporting.
 */
@injectable()
export class DesignImplementationFlowReadService {
  constructor(
    @inject(TYPES.DesignImplementationFlowRepository) 
    private readonly flowRepository: DesignImplementationFlowRepository
  ) {}

  /**
   * Get flow by ID with full details
   */
  async getFlowById(flowId: string): Promise<FlowView | null> {
    const flow = await this.flowRepository.findById(flowId);
    if (!flow) return null;

    return this.mapToFlowView(flow);
  }

  /**
   * Query flows with criteria and pagination
   */
  async queryFlows(
    criteria: FlowQueryCriteria = {},
    limit: number = 50,
    offset: number = 0
  ): Promise<FlowQueryResult> {
    // Get all flows (in a real implementation, this would be optimized with MongoDB queries)
    let flows = await this.flowRepository.findAll();

    // Apply filters
    if (criteria.projectId) {
      flows = flows.filter(f => f.projectId === criteria.projectId);
    }
    
    if (criteria.status) {
      flows = flows.filter(f => f.status.toString() === criteria.status);
    }
    
    if (criteria.createdBy) {
      flows = flows.filter(f => f.createdBy === criteria.createdBy);
    }
    
    if (criteria.artifactType) {
      flows = flows.filter(f => 
        f.artifacts.some(a => a.type === criteria.artifactType)
      );
    }
    
    if (criteria.hasConformanceIssues) {
      flows = flows.filter(f => 
        f.artifacts.some(a => 
          a.conformanceScore && a.conformanceScore.needsImprovement()
        )
      );
    }

    const totalCount = flows.length;
    const paginatedFlows = flows.slice(offset, offset + limit);
    const flowViews = paginatedFlows.map(f => this.mapToFlowView(f));

    return {
      flows: flowViews,
      totalCount,
      hasMore: offset + limit < totalCount
    };
  }

  /**
   * Get flows by project ID
   */
  async getFlowsByProjectId(projectId: string): Promise<FlowView[]> {
    const flows = await this.flowRepository.findByProjectId(projectId);
    return flows.map(f => this.mapToFlowView(f));
  }

  /**
   * Get active flows
   */
  async getActiveFlows(): Promise<FlowView[]> {
    const flows = await this.flowRepository.findActiveFlows();
    return flows.map(f => this.mapToFlowView(f));
  }

  /**
   * Get flows that need attention
   */
  async getFlowsNeedingAttention(): Promise<FlowView[]> {
    const flows = await this.flowRepository.findActiveFlows();
    const needingAttention = flows.filter(flow => {
      const blocked = flow.getBlockedArtifacts();
      return blocked.length > 0;
    });
    
    return needingAttention.map(f => this.mapToFlowView(f));
  }

  /**
   * Get conformance summary across all flows
   */
  async getConformanceSummary(): Promise<{
    totalFlows: number;
    activeFlows: number;
    completedFlows: number;
    totalArtifacts: number;
    averageConformance: number;
    artifactsNeedingAttention: number;
  }> {
    const flows = await this.flowRepository.findAll();
    
    let totalArtifacts = 0;
    let totalConformanceSum = 0;
    let artifactsWithScores = 0;
    let artifactsNeedingAttention = 0;

    flows.forEach(flow => {
      const metrics = flow.getProgressMetrics();
      totalArtifacts += metrics.totalArtifacts;
      
      flow.artifacts.forEach(artifact => {
        if (artifact.conformanceScore) {
          totalConformanceSum += artifact.conformanceScore.overallScore;
          artifactsWithScores++;
          
          if (artifact.conformanceScore.needsImprovement()) {
            artifactsNeedingAttention++;
          }
        }
      });
    });

    return {
      totalFlows: flows.length,
      activeFlows: flows.filter(f => f.isActive()).length,
      completedFlows: flows.filter(f => f.isCompleted()).length,
      totalArtifacts,
      averageConformance: artifactsWithScores > 0 
        ? Math.round(totalConformanceSum / artifactsWithScores) 
        : 0,
      artifactsNeedingAttention
    };
  }

  private mapToFlowView(flow: any): FlowView {
    const progressMetrics = flow.getProgressMetrics();
    const blockedArtifacts = flow.getBlockedArtifacts();

    return {
      flowId: flow.id,
      projectId: flow.projectId,
      status: flow.status.toString(),
      createdBy: flow.createdBy,
      createdAt: flow.createdAt.toISOString(),
      completedAt: flow.completedAt?.toISOString(),
      
      metaModelBinding: flow.metaModelBinding ? {
        metamodelId: flow.metaModelBinding.metamodelId,
        boundAt: flow.metaModelBinding.boundAt.toISOString(),
        validationStatus: flow.metaModelBinding.validationStatus
      } : undefined,
      
      designSpecification: flow.designSpecification,
      
      progressMetrics,
      blockedArtifacts,
      
      artifacts: flow.artifacts.map((artifact: any) => ({
        id: artifact.id,
        name: artifact.name,
        type: artifact.type,
        version: artifact.version,
        status: artifact.status.toString(),
        conformanceScore: artifact.conformanceScore ? {
          metaModelScore: artifact.conformanceScore.metaModelScore,
          designCoverageScore: artifact.conformanceScore.designCoverageScore,
          overallScore: artifact.conformanceScore.overallScore,
          confidence: artifact.conformanceScore.confidence
        } : undefined,
        healthStatus: {
          buildStatus: artifact.healthStatus.buildStatus,
          testStatus: artifact.healthStatus.testStatus,
          deploymentStatus: artifact.healthStatus.deploymentStatus,
          lastHealthCheck: artifact.healthStatus.lastHealthCheck.toISOString()
        },
        tags: artifact.tags
      }))
    };
  }
}